import type { PayloadAction } from '@reduxjs/toolkit';
import type { User } from '@/domains/user/types/User';
import type { SaveUserCustomInboxRequest } from '@/domains/user/types/api/requests/SaveUserCustomInboxRequest';
import { CustomInbox } from '@/features/customInbox/types/CustomInbox';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import { call, delay, put, takeEvery, takeLatest } from 'redux-saga/effects';
import CustomInboxEvents from '@/features/customInbox/events';
import CustomInboxService from '@/features/customInbox/services/CustomInboxService';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import {
  deleteUserCustomInbox,
  getUserCustomInboxes,
  getUserCustomInboxesSuccess,
  getUserData,
  getUserDataSuccess,
  saveUserCustomInboxes,
} from '@/domains/user/store/userSlice';
import { CacheVariables } from '@/enums/CacheVariables';
import { ApiDomainPaths } from '@/enums/apiDomainPaths';
import CacheService from '@/services/CacheService/CacheService';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';

function* getUserDataSaga(action?: PayloadAction<{ resetCache?: boolean }>) {
  try {
    const cachedUserData = CacheService.get<User>(CacheVariables.UserInfo, Boolean(action?.payload?.resetCache));
    if (cachedUserData) return yield put(getUserDataSuccess(cachedUserData));

    yield put(setSystemLoadingStatus(true));
    const { data }: HttpServiceResponse<User> = yield call(HttpService.get, `${ApiDomainPaths.ACCOUNT}/userinfo`);

    CacheService.set<User>(CacheVariables.UserInfo, data);

    yield put(getUserDataSuccess(data));
  } catch (error) {
    console.error(error);
    yield delay(3000);
    yield call(getUserDataSaga);
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

function* saveUserCustomInboxSaga({ payload }: PayloadAction<SaveUserCustomInboxRequest>) {
  try {
    yield call(HttpService.post, '/Account/inboxes', payload);
  } catch (error) {
    console.error(error);
  } finally {
    ModalService.close(Modals.AddCustomInbox);
    yield delay(300);
    EventService.dispatch(new CustomInboxEvents.UpdateCustomInboxes({}));
  }
}

function* getUserCustomInboxesSaga({
  payload: { location, resetCache },
}: PayloadAction<{ location: string; resetCache?: boolean }>) {
  try {
    const cacheKey = [CacheVariables.UserInboxes, location].join('_');
    const cachedInboxes = CacheService.get<Record<string, CustomInbox>>(cacheKey, Boolean(resetCache));
    if (cachedInboxes)
      return yield put(getUserCustomInboxesSuccess(CustomInboxService.normalizeResponseRecord(cachedInboxes)));

    const { data } = yield call(HttpService.get, '/Account/inboxes', { location });

    CacheService.set<Record<string, CustomInbox>>(cacheKey, data);

    yield put(getUserCustomInboxesSuccess(CustomInboxService.normalizeResponseRecord(data)));
  } catch (error) {
    console.error(error);
  } finally {
    ModalService.close(Modals.AddCustomInbox);
  }
}

function* deleteUserCustomInboxSaga({ payload: id }: PayloadAction<number>) {
  try {
    yield call(HttpService.delete, `/Account/inboxes/${id}`);
  } catch (error) {
    console.error(error);
  } finally {
    yield delay(300);
    EventService.dispatch(new CustomInboxEvents.UpdateCustomInboxes({}));
  }
}

export function* userWatcher() {
  yield takeLatest(getUserData.type, getUserDataSaga);
  yield takeEvery(getUserCustomInboxes.type, getUserCustomInboxesSaga);
  yield takeLatest(saveUserCustomInboxes.type, saveUserCustomInboxSaga);
  yield takeLatest(deleteUserCustomInbox.type, deleteUserCustomInboxSaga);
}
